import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Grid,
  Paper,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  accordionItem: {
    marginBottom: theme.spacing(1.5),
  },
  policySection: {
    marginBottom: theme.spacing(2),
  },
  policyLimitsBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
    borderRadius: theme.spacing(0.5),
  },
  vehicleInfoContainer: {
    marginBottom: theme.spacing(2),
  },
  vehicleInfoBox: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
  },
  coveragesContainer: {
    marginTop: theme.spacing(1),
  },
  coveragesBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  memberInfoPaper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.info.light,
    marginBottom: theme.spacing(2),
  },
  customItemsBox: {
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
      color: theme.palette.mode === 'dark'
      ? theme.palette.grey[800] 
      : theme.palette.grey[900],
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.grey[900],
  },
  policyTitle: {
    fontWeight: 600,
  },
  sectionText: {
    color : theme.palette.mode === 'dark'
      ? theme.palette.grey[800] 
      : theme.palette.grey[900],
  },
}));

const WrapperApiResponseCard = ({ wrapperResponse, classes: dialogClasses }) => {
  const classes = useStyles();
  const [expandedPolicies, setExpandedPolicies] = useState({});

  if (!wrapperResponse?.dataJson?.policies) return null;

  const policies = wrapperResponse.dataJson.policies;
  const statusCode = wrapperResponse.statusCode;

  const handlePolicyExpand = (policyNumber) => {
    setExpandedPolicies(prev => ({
      ...prev,
      [policyNumber]: !prev[policyNumber],
    }));
  };

  const renderPolicyInfo = (policy) => {
    const policyInfo = policy.PolicyInfo || policy.policyInfo;
    if (!policyInfo) return null;

    const limits = policyInfo.PolicyLimits || policyInfo.policyLimits;

    return (
      <Box className={classes.policySection}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Full Policy Number:</strong> {policyInfo.FullPolicyNumber || policyInfo.fullPolicyNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Client Number:</strong> {policyInfo.ClientNumber || policyInfo.clientNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Program:</strong> {policyInfo.ProgramNumber || policyInfo.programNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Product Code:</strong> {policyInfo.ProductCode || policyInfo.productCode}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Effective Date:</strong> {policyInfo.EffectiveDate || policyInfo.effectiveDate}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Expiration Date:</strong> {policyInfo.ExpirationDate || policyInfo.expirationDate}
            </Typography>
          </Grid>
          {(policyInfo.CancellationDate || policyInfo.cancellationDate) && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Cancellation Date:</strong> {policyInfo.CancellationDate || policyInfo.cancellationDate}
              </Typography>
            </Grid>
          )}
        </Grid>

        {limits && (
          <Box className={classes.policyLimitsBox}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Policy Limits
            </Typography>
            <Typography variant="body2" className={classes.sectionText}>
              <strong>Type:</strong> {limits.Type || limits.type} - <strong>Value:</strong> {limits.Value || limits.value} {limits.Unit || limits.unit}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderVehicleInfo = (vehicleInfoArray) => {
    if (!vehicleInfoArray || !Array.isArray(vehicleInfoArray) || vehicleInfoArray.length === 0) {
      return null;
    }

    return (
      <Box className={classes.vehicleInfoContainer}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Vehicle Information
        </Typography>
        {vehicleInfoArray.map((vehicle, vIdx) => (
          <Box key={vIdx} className={classes.vehicleInfoBox}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  <strong>VIN 8:</strong> {vehicle.VIN?.VIN8 || vehicle.vin?.vin8 || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  <strong>Full VIN:</strong> {vehicle.VIN?.FULLVIN || vehicle.vin?.fullVin || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  <strong>Make:</strong> {vehicle.Make || vehicle.make || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  <strong>Model:</strong> {vehicle.Model || vehicle.model || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  <strong>Year:</strong> {vehicle.Year || vehicle.year || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  <strong>Plate:</strong> {vehicle.Plate || vehicle.plate || 'N/A'}
                </Typography>
              </Grid>
              {(vehicle.Coverages || vehicle.coverages) && (
                <Grid item xs={12}>
                  <Box className={classes.coveragesContainer}>
                    <Typography variant="caption" className={classes.sectionTitle}>
                      Active Coverages:
                    </Typography>
                    <Box className={classes.coveragesBox}>
                      {(vehicle.Coverages || vehicle.coverages).map((cov, cIdx) => (
                        (cov.HasCoverage || cov.hasCoverage) && (
                          <Chip
                            key={cIdx}
                            label={cov.CoverageType || cov.coverageType}
                            size="small"
                            color="primary"
                          />
                        )
                      ))}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
      </Box>
    );
  };

  const renderMemberInfo = (memberInfo) => {
    if (!memberInfo) return null;

    return (
      <Paper className={classes.memberInfoPaper}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Member Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Name:</strong> {memberInfo.FirstName || memberInfo.firstName} {memberInfo.LastName || memberInfo.lastName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Phone:</strong> {memberInfo.PhoneNumber || memberInfo.phoneNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Email:</strong> {memberInfo.Email || memberInfo.email || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Address:</strong> {memberInfo.Address || memberInfo.address}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>City, State, Zip:</strong> {memberInfo.City || memberInfo.city}, {memberInfo.State || memberInfo.state} {memberInfo.Zip || memberInfo.zip}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderCustomItems = (customItems) => {
    if (!customItems || customItems.length === 0) return null;

    return (
      <Box className={classes.customItemsBox}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Custom Items
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customItems.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>{item.Name || item.name}</TableCell>
                <TableCell>{item.Value || item.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Card variant="outlined" className={classes.card}>
      <CardHeader
        title="Response from Wrapper API"
        avatar={<ArrowBackIcon />}
        subheader={`Status Code: ${statusCode}`}
        className={dialogClasses.logSearchDialogHeader}
      />
      <CardContent>
        <Box>
          {policies.map((policy, pIdx) => {
            const policyNumber = policy.PolicyInfo?.PolicyNumber || policy.policyInfo?.policyNumber || `Policy ${pIdx + 1}`;
            const isExpanded = expandedPolicies[policyNumber];

            return (
              <Accordion
                key={pIdx}
                expanded={isExpanded}
                onChange={() => handlePolicyExpand(policyNumber)}
                className={classes.accordionItem}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" className={classes.policyTitle}>
                    {policyNumber}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>
                  {renderPolicyInfo(policy)}
                  {renderVehicleInfo(policy.VehicleInfo || policy.vehicleInfo)}
                  {renderMemberInfo(policy.MemberInfo || policy.memberInfo)}
                  {renderCustomItems(policy.CustomItems || policy.customItems)}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

WrapperApiResponseCard.propTypes = {
  wrapperResponse: PropTypes.object,
  classes: PropTypes.object.isRequired,
};

export default WrapperApiResponseCard;
